import type { RailLayout } from "@/data/rail-branches";
import type { LocationRun } from "../LocationRun/runs";
import { sceneStartMarkerCenterX } from "./metrics";
import { type PartRunPhase } from "../../rail/PartRunPhase/phase";
import { upcomingPartAnchorX } from "./placePartUpcoming";
import {
  branchForSiteRun,
  forkTravelerFollowActive,
  forkTravelerStageAnchor,
  forkedBranchRailSlotX,
  siteRunFollowsCastColumn,
  branchForLocationRun,
} from "../LocationRun/siteAnchor";
import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";

function forkTravelerAnchorX(
  run: LocationRun,
  playheadMs: number,
  viewportWidth: number,
  totalDurationMs: number,
  rail?: RailLayout,
): number | undefined {
  if (rail === undefined || !forkTravelerFollowActive(rail, run, playheadMs)) {
    return undefined;
  }
  const branch = branchForLocationRun(run, rail);
  if (branch === undefined) {
    return undefined;
  }
  const anchor = forkTravelerStageAnchor(
    rail,
    branch,
    playheadMs,
    viewportWidth,
    totalDurationMs,
    0,
  );
  return anchor?.anchorX;
}

export function locationRunAnchorX(
  run: LocationRun,
  isActive: boolean,
  playheadMs: number,
  viewportWidth: number,
  totalDurationMs: number,
  rail?: RailLayout,
): number {
  const travelerX = forkTravelerAnchorX(
    run,
    playheadMs,
    viewportWidth,
    totalDurationMs,
    rail,
  );
  if (travelerX !== undefined) {
    return travelerX;
  }
  const followCast = isActive
    || (rail !== undefined && siteRunFollowsCastColumn(rail, run, playheadMs));
  if (followCast) {
    return computeSceneMotion(
      playheadMs,
      viewportWidth,
      totalDurationMs,
    ).groupCenterX;
  }
  if (playheadMs >= run.endMs) {
    return computeSceneMotion(
      run.endMs,
      viewportWidth,
      totalDurationMs,
    ).groupCenterX;
  }
  const branch = rail === undefined
    ? undefined
    : branchForSiteRun(run, rail);
  const railMs = run.startMs;
  if (branch !== undefined) {
    const forkX = forkedBranchRailSlotX(
      rail!,
      branch,
      railMs,
      viewportWidth,
      totalDurationMs,
    );
    if (forkX !== undefined) {
      return forkX;
    }
  }
  return sceneStartMarkerCenterX(railMs, viewportWidth, totalDurationMs);
}

export function partLocationRunAnchorX(
  run: LocationRun,
  phase: PartRunPhase,
  playheadMs: number,
  viewportWidth: number,
  totalDurationMs: number,
  rail?: RailLayout,
): number {
  if (phase.kind === "active") {
    return computeSceneMotion(
      playheadMs,
      viewportWidth,
      totalDurationMs,
    ).groupCenterX;
  }
  if (phase.kind === "upcoming") {
    return upcomingPartAnchorX(
      run,
      phase.railStartMs,
      viewportWidth,
      totalDurationMs,
      rail,
      forkTravelerAnchorX(
        run,
        playheadMs,
        viewportWidth,
        totalDurationMs,
        rail,
      ),
    );
  }
  return computeSceneMotion(
    phase.handoffMs,
    viewportWidth,
    totalDurationMs,
  ).groupCenterX;
}
